import { useEffect } from 'react';
import { IndividualsDuplexerChannel } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { pendingSettleFor, throughPendingSettle, type PendingSettle } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { subscribeToChannel, type DuplexerEventName } from './duplexer-connection';
import { optimisticallySettled } from './optimistic-settle';
import { queryClient, queryKeys } from './query-client';

/**
 * ONE agent-task listener for the whole app, and it INVALIDATES rather than refetches.
 *
 * ## The defect this replaces
 *
 * `useAgentTaskDuplexer` registers a listener per hook INSTANCE (`useDuplexerSubscription` has no dedup),
 * and every task-backed hook's listener called `query.refetch()` on a SHARED React Query key. On the home
 * screen `useAgentWorkspaceTasks` alone mounts four times (the island badge directly, plus via
 * `useIslandState`, plus twice via `useIslandAgentStarted`), each fanning out over every workspace; the
 * quick-view and `useTasks` add their own on `queryKeys.tasks(undefined)`. One duplexer frame therefore ran
 * N refetches of the same cache entries inside a single `Set.forEach` tick — and `refetch()` defaults to
 * `cancelRefetch: true`, so they CANCEL AND RESTART each other instead of coalescing. Measured in
 * production over 2.5 days: 6,602 `runaway-fetch` reports, `key=account-wide`, `subs=8–23`, and
 * `calls=15` inside 89 ms. Not a poll — a synchronous storm, on `/api/tasks`, whose p999 is 6.4 s. That is
 * the "island loading for a long time" report.
 *
 * ## The shape of the fix
 *
 * One listener, ref-counted at module level, calling `queryClient.invalidateQueries` ONCE per event.
 * React Query then refetches each ACTIVE key exactly once, which is what a shared cache entry assumed all
 * along. Invalidate is strictly WEAKER than a forced refetch — it marks stale and refetches the observers
 * that exist, where `refetch()` also drives disabled/inactive queries — so the freshness contract the
 * push carries is preserved while the multiplier is gone.
 *
 * ## Why ref-counted here and not deduped inside `useDuplexerSubscription`
 *
 * Deduping in that hook was considered and REJECTED: its consumers pass genuinely different callbacks
 * (`lib/goals.ts` refetches a workspace's goals, `lib/use-goal-ledger.ts` one goal's ledger,
 * `lib/piled-up-jobs.ts` re-runs an imperative `load()`, `pages/apps.tsx` invalidates `workspace-apps`),
 * and a hook that collapsed them would silently drop real subscribers. The amplifier is not "many
 * listeners" — it is "many listeners doing the SAME work on a SHARED key", which is a property of the
 * task query layer, not of the transport.
 *
 * **What this covers**: every reader of `queryKeys.tasks(*)` and `queryKeys.workspaceTasks(*)` —
 * `useTasks`, `useWorkspaceTasks`, `useAgentWorkspaceTasks`, `useIslandQuickView`, and anything that
 * later renders from those keys, because it is the KEY that is invalidated, not a hook instance.
 * **What it does NOT cover**: the other `useAgentTaskDuplexer` consumers listed above, which each drive
 * their own distinct key and are one-per-screen rather than N-per-screen. They keep their own listener.
 */

const ENTITY_CHANGE_EVENTS: DuplexerEventName[] = ['created', 'updated', 'deleted'];

/**
 * Mark every task-backed cache entry stale: two prefix sweeps for the LIST keys (the account-wide +
 * per-workspace `tasks` scopes, and every `workspace-tasks` entry), plus — when the caller names a task —
 * that one task's three per-task entries.
 *
 * Exported so a mutation path reuses the exact keys the push invalidates: a caller cannot forget a cache
 * it does not have to list.
 *
 * **`taskId` invalidates ONE entry per family, never the prefix, and that is load-bearing.** The list
 * keys are shared — one entry serves every reader — so a prefix sweep costs one refetch. The per-task
 * keys are one entry PER TASK, so sweeping `['task-detail']` would refetch every task the session has
 * ever opened, on every frame. That is the same multiplier this module was written to remove (6,602
 * `runaway-fetch` reports; see above), arriving from the other direction.
 */
/**
 * Apply the reader's answer to every cached copy of the task, NOW, before the server is asked.
 *
 * The sibling of {@link invalidateAgentTaskQueries}, and it lives beside it for the reason that module
 * exists: a caller cannot forget a cache it does not have to list. Invalidating alone is not enough
 * here — settling writes `blockedByDecisions` and never the task's status, so the refetch this triggers
 * returns the SAME badge it already had, and the row only moves when an agent later picks it up. That
 * gap is what reads as "many seconds until the other indications reflect it".
 *
 * Patching is safe because it is not a guess: the block really is gone the moment the answer is
 * recorded, and {@link optimisticallySettled} claims nothing beyond it — the row becomes Active Task,
 * never Working. The server's next frame replaces it wholesale either way, and the caller holds the
 * originals so a failed settle can put them back.
 */
export function settleDecisionInAgentTaskCaches(decisionId: string, choice?: string): void {
  const now = Date.now();
  const patch = <T extends { decisionPrompts?: { decisionId: string }[]; displayStatus?: unknown }>(task: T) => {
    const pending = pendingSettleFor(task as never, decisionId, choice, now);
    if (pending) pendingSettles.set(pending.taskId, pending);
    return optimisticallySettled(task, decisionId, choice);
  };
  eachTaskCache((task) => patch(task as never));
  if (pendingSettles.size > 0) retainPendingSettleHold();
}

/** Apply `edit` to every cached copy of every task: the list families by prefix, and each detail entry. */
function eachTaskCache(edit: (task: Record<string, unknown>) => Record<string, unknown>): void {
  // The LIST families, by prefix: one entry serves every reader, and the answered row can sit in any of
  // them (account-wide and per-space hold different pages of the same task).
  for (const key of [queryKeys.tasksAllScopes, queryKeys.workspaceTasksAllScopes]) {
    queryClient.setQueriesData<unknown[]>({ queryKey: key }, (rows) =>
      Array.isArray(rows) ? rows.map((row) => edit(row as Record<string, unknown>)) : rows,
    );
  }
  // And the single-task entries, which have no duplexer subscription of their own — an open detail sheet
  // would otherwise keep its old bucket until the reader acted in it again.
  queryClient.setQueriesData<Record<string, unknown>>({ queryKey: ['task-detail'] }, (task) => (task ? edit(task) : task));
}

/**
 * Answers whose status change the server has not shown yet, by task id (see `PendingSettle`). A hand-back
 * tap moves the card at once, but the re-read the answer triggers, and the push for the decision's release,
 * both land before the server's transition and would put the card straight back on Action needed.
 */
const pendingSettles = new Map<string, PendingSettle>();
let disposePendingSettleHold: (() => void) | null = null;

/** Read lazily, not at module load: suites that mock `query-client` with a partial `queryKeys` import this module too. */
function isTaskCacheRoot(root: unknown): boolean {
  return root === queryKeys.tasksAllScopes[0] || root === queryKeys.workspaceTasksAllScopes[0] || root === 'task-detail';
}

/** A row seen through any pending answer: the answered status kept while the server has not caught up. */
function heldRow(row: Record<string, unknown>, now: number): Record<string, unknown> {
  const pending = typeof row.id === 'string' ? pendingSettles.get(row.id) : undefined;
  if (!pending) return row;
  const { task, held } = throughPendingSettle(row as never, pending, now);
  if (!held) {
    pendingSettles.delete(pending.taskId);
    return row;
  }
  // Same reason `optimisticallySettled` drops it: the server's verdict outranks `status` at every read site.
  const { displayStatus: _staleVerdict, ...rest } = task as Record<string, unknown>;
  return rest;
}

/**
 * Re-apply the pending answers to every task read that lands, until each one is spent. Writes made by
 * `setQueryData` (ours included) are skipped: re-reading our own patched row would see the new status,
 * take it for the server's, and drop the hold it is still needed for.
 */
function retainPendingSettleHold(): void {
  if (disposePendingSettleHold) return;
  disposePendingSettleHold = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual) return;
    if (!isTaskCacheRoot(event.query.queryKey[0])) return;
    const now = Date.now();
    // Spent by time even when no read of that task ever lands again (a list that no longer carries it), so the
    // map cannot grow for the session and the listener is released once nothing is left to hold.
    for (const [taskId, pending] of pendingSettles) {
      if (now > pending.untilMs) pendingSettles.delete(taskId);
    }
    const data = event.query.state.data as unknown;
    const next = Array.isArray(data)
      ? data.map((row) => heldRow(row as Record<string, unknown>, now))
      : data && typeof data === 'object'
        ? heldRow(data as Record<string, unknown>, now)
        : data;
    const changed = Array.isArray(data) ? (next as unknown[]).some((row, i) => row !== data[i]) : next !== data;
    if (changed) queryClient.setQueryData(event.query.queryKey, next);
    if (pendingSettles.size === 0) {
      disposePendingSettleHold?.();
      disposePendingSettleHold = null;
    }
  });
}

/** Test seam: the answers still waiting for the server to show them. */
export function pendingSettleCount(): number {
  return pendingSettles.size;
}

export function invalidateAgentTaskQueries(taskId?: string): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.tasksAllScopes });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workspaceTasksAllScopes });
  if (!taskId) return;
  void queryClient.invalidateQueries({ queryKey: queryKeys.taskDetail(taskId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.taskAtWork(taskId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.taskAgentConversation(taskId) });
  // `task-liveness` has no duplexer subscription of its own either, so a cancel/stop otherwise left the
  // open detail modal's live marker up for the whole of its ~20s poll — the same defect `task-detail`
  // had, one key over. Caught in review.
  void queryClient.invalidateQueries({ queryKey: queryKeys.taskLiveness(taskId) });
}

let subscriberCount = 0;
let disposeSubscription: (() => void) | null = null;

/**
 * Retain the shared listener; the returned disposer releases this holder's claim. The channel
 * subscription is opened on the FIRST retainer and dropped on the last release, so N mounted readers
 * still fan ONE event out to ONE invalidate.
 */
export function retainAgentTaskInvalidation(): () => void {
  subscriberCount += 1;
  if (subscriberCount === 1) {
    // The frame's `entityId` is the task that changed, so the one open detail / at-work / transcript entry
    // for it is refreshed too. The channel also carries events whose `entityId` is NOT a task (a
    // maestro-action nudge names a conversation); an id that matches no cached entry invalidates nothing,
    // so the filter is simply the key lookup.
    disposeSubscription = subscribeToChannel(IndividualsDuplexerChannel.AGENT_TASK, ENTITY_CHANGE_EVENTS, (payload) =>
      invalidateAgentTaskQueries((payload as { entityId?: string })?.entityId),
    );
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    subscriberCount -= 1;
    if (subscriberCount === 0) {
      disposeSubscription?.();
      disposeSubscription = null;
    }
  };
}

/**
 * What a task-backed hook takes INSTEAD of its own `useAgentTaskDuplexer(() => refetch())`. Mounting it
 * from ten components costs one listener and one invalidate per event.
 *
 * HTTPS-only and client-only, matching `useDuplexerSubscription` — the shared duplexer connection cannot
 * be opened over plain http, and there is no socket under SSR.
 */
export function useAgentTaskInvalidation(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.location.protocol !== 'https:') return undefined;
    return retainAgentTaskInvalidation();
  }, []);
}

/** Test seam: how many holders the shared listener currently has. Always 0 or 1 subscription behind it. */
export function agentTaskInvalidationHolders(): number {
  return subscriberCount;
}
